/**
 * Reference documents (spec §2 C): title-and-text rows the agent reads before
 * every run. Only the first MAX_DOC_CHARS characters of each are read
 * (migration 956 saves no more), and each row says so. The one editor for
 * them, mounted by the settings page (ReadsSection) and by the agent creator.
 * Controlled: the mounting screen owns the rows and their save.
 */

import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { newReferenceDoc, type AgentReferenceDoc } from '../../../lib/agentSettingsService';
import { MAX_DOC_CHARS } from '../../../../supabase/functions/_shared/referenceDocFence.ts';

export function ReferenceDocsEditor({ docs, onChange, readOnly, placeholder }: {
  docs: AgentReferenceDoc[];
  onChange: (next: AgentReferenceDoc[]) => void;
  readOnly?: boolean;
  placeholder: string;
}) {
  const put = (i: number, patch: Partial<AgentReferenceDoc>) => {
    const next = [...docs];
    next[i] = { ...docs[i], ...patch };
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid="reference-docs-editor">
      {docs.map((d, i) => (
        <div key={d.id} className="border border-[var(--rule)] p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input value={d.title} placeholder="Title" readOnly={readOnly} aria-label="Document title"
              onChange={(e) => put(i, { title: e.target.value })}
              className="flex-1 px-2 py-1 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-[var(--mustard)]" />
            {!readOnly && (
              <Button variant="ghost" size="sm" onClick={() => onChange(docs.filter((_, j) => j !== i))} title="Remove" aria-label="Remove document"><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
          <textarea value={d.content} rows={4} placeholder={placeholder} readOnly={readOnly} aria-label="Document text"
            onChange={(e) => put(i, { content: e.target.value })}
            className="w-full px-2 py-1 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-[var(--mustard)] resize-y" />
            <p className="text-[11px] text-[var(--text-ter)]">Only the first {MAX_DOC_CHARS.toLocaleString('en-US')} characters are read.</p>
        </div>
      ))}
      {!docs.length && <p className="text-xs text-[var(--text-ter)]">None yet.</p>}
      {!readOnly && (
        <Button variant="secondary" size="sm" onClick={() => onChange([...docs, newReferenceDoc()])}>
          <Plus className="h-3 w-3" /> Add a document
        </Button>
      )}
    </div>
  );
}
